import throttle from 'lodash/throttle';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Platform, StyleSheet, View } from 'react-native';
import { useMediaQuery } from 'react-responsive';

import {
  useConsumerQuery,
  useUpdateConsumerMutation,
} from 'apollo/queries/autogenerate/hooks';
import prefectures from 'shared/constants/prefectures';
import Button from 'shared/modules/common/components/atoms/Button';
import TextInput from 'shared/modules/common/components/atoms/TextInput';
import Picker from 'shared/modules/common/components/molecules/Picker';
import validator, { Rule } from 'shared/utils/validator';

type Address = {
  city: string;
  state: string;
  zip: string;
  /**
   * apartment
   */
  line1: string;
  /**
   * street/block
   */
  line2: string;
};

type Props = {
  /**
   * Invoked when editing is successful
   */
  onSuccess?: () => void;
};

const fieldNames: Array<keyof Address> = [
  'zip',
  'state',
  'city',
  'line2',
  'line1',
];

const fieldLabels = [
  '郵便番号',
  '都道府県',
  '市区町村',
  '丁目・番地',
  '建物名・部屋番号（任意）',
];

const fieldPlaceholders = [
  '1080025',
  '選択',
  '港区六本木',
  '１−２−３',
  'マンション名１０１号',
];

const EditUserAddressForm = (props: Props) => {
  const isBigScreen = useMediaQuery({ minWidth: 768 });

  const { refetch, loading } = useConsumerQuery({
    fetchPolicy: 'network-only',
    skip: true,
  });

  const [updateConsumerMutation] = useUpdateConsumerMutation({
    update(cache) {
      refetch({
        masked: true,
      }).then(({ data }) => {
        cache.modify({
          fields: {
            consumer(cachedData) {
              return {
                ...cachedData,
                ...data?.consumer,
              };
            },
          },
        });
      });
    },
  });

  const form = useFormWithValidations({
    city: '',
    line1: '',
    line2: '',
    state: '',
    zip: '',
  });

  useEffect(() => {
    refetch({
      masked: false,
    }).then(({ data }) => {
      const defaultValues = fieldNames.reduce((values, fieldName) => {
        return {
          ...values,
          ...{ [fieldName]: data.consumer.address?.[fieldName] },
        };
      }, {} as Address);

      // We set the default values here
      // So any changes afterwards will be treated "dirty" and can be handled appropriately
      form.reset(defaultValues);
    });
  }, []);

  const submit = useCallback(
    throttle(
      async () => {
        try {
          await updateConsumerMutation({
            variables: {
              address: form.getValues(),
            },
          });

          props.onSuccess?.();
        } catch (err) {
          /* noop */
        }
      },
      2000,
      { leading: true, trailing: false }
    ),
    []
  );

  return (
    <View
      style={style.wrapper}
      {...Platform.select({ web: { accessibilityRole: 'form' } })}
    >
      {fieldNames.map((field, index) => {
        if (field === 'state') {
          return (
            <Picker
              key={index}
              value={form.getValues(field)}
              label={fieldLabels[index]}
              placeholder={fieldPlaceholders[index]}
              options={prefectures.map((place) => ({
                label: place,
                value: place,
              }))}
              selectButtonText="保存する"
              onSelect={(text) => form.setValue(field, text)}
              onBlur={() => form.triggerValidation(field)}
              message={(form.errors[field]?.message as string) || ''}
              state={form.errors[field]?.message ? 'error' : undefined}
              editable={!loading}
              style={style.textInput}
              dialogStyle={style.pickerDialog}
              showAsDialog={isBigScreen}
              id={`edit-user-address-${field}-picker`}
            />
          );
        }

        return (
          <TextInput
            key={index}
            value={form.getValues(field)}
            label={fieldLabels[index]}
            placeholder={fieldPlaceholders[index]}
            onChangeText={(text) => form.setValue(field, text)}
            onBlur={() => form.triggerValidation(field)}
            message={(form.errors[field]?.message as string) || ''}
            state={form.errors[field]?.message ? 'error' : undefined}
            editable={!loading}
            style={style.textInput}
            id={`edit-user-address-${field}-field`}
          />
        );
      })}
      <View style={style.spacer} />
      <Button
        title="次へ"
        onPress={async () => {
          const isValid = await form.triggerValidation();

          isValid && submit();
        }}
        style={style.nextButton}
        disabled={!form.formState.dirty}
        id="edit-user-address-submit-button"
      />
    </View>
  );
};

const useFormWithValidations = (props: Address) => {
  const form = useForm<Address>({
    defaultValues: props,
  });

  const basicRules: Rule[] = [
    'mustNotBeEmptyOrSpace',
    'mustNotContainSpace',
    'mustNotContainNonPrintableChars',
  ];

  const zipRules: Rule[] = [...basicRules, 'mustBeZipCode'];

  const line1Rules: Rule[] = [basicRules[2]];

  const validatorRule = (fieldName: string) => {
    let rules = basicRules;

    if (fieldName === fieldNames[4]) {
      // line1
      rules = line1Rules;
    }

    if (fieldName === fieldNames[0]) {
      // zip
      rules = zipRules;
    }

    return validator.getRules(rules);
  };

  useEffect(() => {
    fieldNames.forEach((name) => {
      form.register(
        {
          name,
        },
        {
          validate: validatorRule(name),
        }
      );
    });
  }, [form.register]);

  form.watch();

  return form;
};

const style = StyleSheet.create({
  wrapper: {
    flex: 1,
    justifyContent: 'space-between',
  },
  spacer: {
    marginBottom: 24,
  },
  textInput: {
    marginBottom: 24,
  },
  pickerDialog: {
    width: 424,
  },
  nextButton: {
    alignSelf: 'center',
  },
});

export default EditUserAddressForm;
